import React, { Component, createRef, RefObject } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { Grid, IconButton, RootRef, Tooltip } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';
import LinkIcon from '@material-ui/icons/Link';
import Clipboard from 'clipboard';

import { ShareType, ShortenedUrLs, ShareContentDestination, Network } from 'api';
import { CustomSVG } from 'components';
import * as paths from 'paths';
import { queryString } from 'utilities';
import { isUrl } from 'features/content/helpers';
import {
  withProtectedClient,
  withContent,
  withNotify,
  withUser,
  IWithProtectedClient,
  IWithContent,
  IWithNotify,
  IWithUser,
} from 'providers';
import { callShareContentMutation } from 'app-react-query/imperative';

interface IProps {
  shortenedUrls: ShortenedUrLs;
}

interface IPropsEnhanced
  extends IProps,
    IWithProtectedClient,
    IWithContent,
    IWithNotify,
    IWithUser,
    WithTranslation,
    WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    button: {
      justifyContent: 'flex-start',
      padding: '8px 16px',
      fontWeight: theme.typography.fontWeightRegular,
      backgroundColor: theme.palette.common.white,
      color: theme.custom.palette.grey1,
      border: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '&:hover': {
        color: 'inherit',
      },
    },
    image: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    text: theme.mixins.visuallyHidden,
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  withContent,
  withProtectedClient,
  withUser,
  withNotify,
  withTranslation(),
  withStyles(styles)
);

class ActionButtons extends Component<IPropsEnhanced> {
  contentClipboard: Clipboard;
  contentButton: RefObject<HTMLButtonElement>;
  contentText: RefObject<HTMLSpanElement>;

  esClipboard: Clipboard;
  esButton: RefObject<HTMLButtonElement>;
  esText: RefObject<HTMLSpanElement>;

  constructor(props: IPropsEnhanced) {
    super(props);
    this.contentButton = createRef();
    this.contentText = createRef();
    this.esButton = createRef();
    this.esText = createRef();
  }

  componentDidMount() {
    const hasUrl = isUrl(this.props.content);

    this.initESClipboard();
    hasUrl && this.initContentClipboard();
  }

  componentWillUnmount() {
    this.contentClipboard && this.contentClipboard.destroy();
    this.esClipboard && this.esClipboard.destroy();
  }

  initContentClipboard() {
    const { content, notify } = this.props;
    const { current } = this.contentButton;

    this.contentClipboard = new Clipboard(current, {
      target: () => this.contentText.current,
    });

    async function onSuccess() {
      notify.info({ i18n: { i18nKey: 'sharing:copied' } });

      const variables = {
        content_id: content.content_id,
        items: [
          {
            share_type: ShareType.Frontend,
            destination: ShareContentDestination.Direct,
          },
        ],
      };

      await callShareContentMutation(variables);
    }

    this.contentClipboard.on('success', () => void onSuccess());
  }

  initESClipboard() {
    const { notify } = this.props;
    const { current } = this.esButton;

    this.esClipboard = new Clipboard(current, {
      target: () => this.esText.current,
    });

    this.esClipboard.on('success', () => {
      notify.info({ i18n: { i18nKey: 'sharing:copied' } });
    });
  }

  getMetadata() {
    const { content } = this.props;
    const { content_object } = content;
    const { url_metadata } = content_object;

    return url_metadata;
  }

  handleEmailTracking = async () => {
    const { content } = this.props;

    const variables = {
      content_id: content.content_id,
      items: [
        {
          share_type: ShareType.Frontend,
          destination: ShareContentDestination.Email,
        },
      ],
    };

    await callShareContentMutation(variables);
  };

  renderEmailButton() {
    const { client, content, shortenedUrls, t, user } = this.props;
    const { email_domains } = client;
    const [emailDomain] = email_domains;

    const { title, description } = this.getMetadata();
    const { commentary } = content;
    const { email } = shortenedUrls;
    const emailParams = {
      from: user.email_address,
      subject: title,
      body: '',
    };

    let linkedinCommentary = null;
    if (commentary) {
      linkedinCommentary = commentary.find(comment => comment.network === Network.Linkedin);
    }

    if (linkedinCommentary) {
      emailParams.body = `${linkedinCommentary.commentary}%0A%0A${email.short_link}`;
    } else {
      emailParams.body = `${description}%0A%0A${email.short_link}`;
    }

    const mailtoLink = `mailto:colleague@${emailDomain}?${queryString(emailParams)}`;

    return (
      <Grid item>
        <Tooltip title={t('common:email')} placement="top">
          <IconButton href={mailtoLink} onClick={this.handleEmailTracking}>
            <EmailIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  renderContentButton() {
    const { classes, shortenedUrls, t } = this.props;
    const { direct } = shortenedUrls;
    const metadata = this.getMetadata();
    const tooltip = t('sharing:copyProviderLink', { name: metadata.provider_name });

    return (
      <Grid item>
        <span className={classes.text} ref={this.contentText}>
          {direct.short_link}
        </span>
        <RootRef rootRef={this.contentButton}>
          <Tooltip title={tooltip} placement="top">
            <IconButton>
              <LinkIcon />
            </IconButton>
          </Tooltip>
        </RootRef>
      </Grid>
    );
  }

  renderESButton() {
    const { classes, content, t } = this.props;
    const { content_id } = content;

    const part = paths.content.replace(':content_id', content_id);
    const contentPath = `${window.location.origin}${part}`;
    const tooltip = t('sharing:copyEveryoneSocialLink');

    return (
      <Grid item>
        <span className={classes.text} ref={this.esText}>
          {contentPath}
        </span>
        <RootRef rootRef={this.esButton}>
          <Tooltip title={tooltip} placement="top">
            <IconButton>
              <CustomSVG name="everyoneSocialSolid" />
            </IconButton>
          </Tooltip>
        </RootRef>
      </Grid>
    );
  }

  render() {
    const hasUrl = isUrl(this.props.content);

    return (
      <Grid container spacing={1}>
        {hasUrl && this.renderEmailButton()}
        {hasUrl && this.renderContentButton()}
        {this.renderESButton()}
      </Grid>
    );
  }
}

export default enhancers(ActionButtons);
